// Import user role types from dedicated file
import { UserRoleType } from './userRoles';

// Tenant status types
export const TenantStatus = {
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
} as const;

export type TenantStatus = (typeof TenantStatus)[keyof typeof TenantStatus];

// Twilio Credentials type
export interface TwilioCredentials {
    twilioAccountSID: string;
    twilioAPIKey: string;
    twilioAPIKeySecret: string;
}

// LawRuler Credentials type
export interface LawRulerCredentials {
    portal?: string;
    clientId?: string;
    clientSecret?: string;
    username?: string;
    password?: string;
}

// CRM Credentials type
export interface CrmCredentials {
    lawruler?: LawRulerCredentials;
}

// Tenant configuration type
export interface TenantConfig {
    status?: TenantStatus;
    twilioCredentials?: TwilioCredentials;
    dashboardData?: {
        infos?: string[];
        charts?: string[];
        metrics?: string[];
    };
    crmCredentials?: CrmCredentials;
    businessHours?: {
        startTime?: string;
        endTime?: string;
    };
}

// Tenant edges type (relations to other entities)
export interface TenantEdges {
    users?: User[];
    // Add other relations as needed
}

// Workflow type
export interface Workflow {
    id: string;
    title: string;
}

// Lightweight workflow summary returned by assignment endpoints
export interface WorkflowSummary {
    id: string;
    title: string;
}

// Main Tenant type
export interface Tenant {
    id: string; // UUID as string in frontend
    name: string;
    outboundTrunkSid?: string;
    phoneNumber?: string;
    transferNumber?: string;
    created_at: string; // ISO date string
    deleted_at?: string | null; // Nullable for soft deletes
    updated_at: string;
    config?: TenantConfig;
    edges?: TenantEdges;
    users?: User[];
    workflows?: Workflow[];
}

// User type
export interface User {
    id: string; // UUID as string in frontend
    first_name: string;
    last_name: string;
    email: string;
    password?: string; // Optional, might not be returned from API
    roles: UserRoleType[];
    tenant_id?: string;
    created_at: string;
    deleted_at?: string | null;
    updated_at: string;
    cognito_id?: string;
    edges?: Record<string, unknown>;
    workflow_assignments?: WorkflowSummary[];
}

// API Response types
export interface GetAccessManagerDataResponse {
    users?: User[];
    tenants?: Tenant[];
    role?: UserRoleType;
}

// Active Account type
export interface ActiveAccount {
    sid: string;
    friendlyName: string;
}

// Outbound Trunk Info type
export interface OutboundTrunkInfo {
    trunk_id: string;
    name: string;
    phone_numbers: string[];
    address: string;
}

// Request types
export interface CreateTenantRequest {
    name: string;
    twilioSubAccountSID: string;
    outboundTrunkSid: string;
    phoneNumber: string;
    transferNumber: string;
    tenantConfig: TenantConfig;
}

export interface CreateUserRequest {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    roles: UserRoleType[];
}

export interface UpdateUserRequest {
    firstName?: string;
    lastName?: string;
    email?: string;
    password?: string;
    roles?: UserRoleType[];
    workflow_ids?: string[] | null;
}

// Delete response type
export interface DeleteResponse {
    message: string;
}

// Masked tenant config types (for the read-only config viewer)

export interface MaskedTwilioCredentials {
    twilioAccountSID?: string;
    twilioAPIKey?: string;
    twilioAPIKeySecret?: string;
    twilioAuthToken?: string;
    businessProfileSid?: string;
    cnamTrustProductSid?: string;
    shakenStirTrustProductSid?: string;
    twilioMessagingServiceSid?: string;
    twilioTrunkSids?: Record<string, string>;
    workflowInboundLivekitTrunks?: Record<string, string>;
}

export interface MaskedLawRulerCredentials {
    portal?: string;
    clientId?: string;
    clientSecret?: string;
    username?: string;
    password?: string;
}

export interface MaskedCrmCredentials {
    lawruler?: MaskedLawRulerCredentials;
}

export interface BusinessHoursConfig {
    startTime?: string;
    endTime?: string;
    timezone?: string;
}

export interface OperatingHoursConfig {
    startTime?: string;
    endTime?: string;
    timezone?: string;
}

export interface StaleCallsConfig {
    eligibleResetMinutes?: number;
    inProgressMinutes?: number;
    transferSeconds?: number;
    dialingMinutes?: number;
    ringingMinutes?: number;
    manualPendingMinutes?: number;
    manualAgentConnectedMinutes?: number;
    manualInProgressMinutes?: number;
    humanOnCallMinutes?: number;
}

export interface CallQueueConfig {
    allowCallsWithoutOnlineAgents?: boolean;
}

export interface MaskedTenantConfig {
    status?: string;
    twilioCredentials?: MaskedTwilioCredentials;
    dashboardData?: Record<string, unknown>;
    crmCredentials?: MaskedCrmCredentials;
    businessHours?: BusinessHoursConfig;
    operatingHours?: OperatingHoursConfig;
    staleCallsConfig?: StaleCallsConfig;
    callQueueConfig?: CallQueueConfig;
    [key: string]: unknown;
}

export interface MaskedTenantConfigResponse {
    id: string;
    name: string;
    created_at: string;
    updated_at: string;
    config: MaskedTenantConfig;
}
